import { motion } from "framer-motion";
import { Skeleton } from "@/components/ui/skeleton";

type AppLoadingScreenProps = {
  variant?: "shell" | "auth" | "data";
  mode?: "login" | "register";
  title?: string;
  subtitle?: string;
  badgeLabel?: string;
};

const loadingStages = [
  "Authenticating your access",
  "Syncing your workspace",
  "Unlocking live marketplace tools",
];

const DashboardWireframe = () => (
  <div className="relative mx-auto w-full max-w-[22rem] rounded-[2rem] border border-slate-300/70 bg-white/90 p-4 shadow-[0_30px_80px_-45px_rgba(15,23,42,0.7)] backdrop-blur">
    <div className="mb-4 flex items-center justify-between">
      <div className="space-y-2">
        <motion.div
          className="h-3 w-20 rounded-full bg-slate-200"
          animate={{ opacity: [0.45, 1, 0.45] }}
          transition={{ duration: 1.8, repeat: Number.POSITIVE_INFINITY }}
        />
        <motion.div
          className="h-2.5 w-28 rounded-full bg-green-100"
          animate={{ width: [96, 112, 96] }}
          transition={{ duration: 2.2, repeat: Number.POSITIVE_INFINITY }}
        />
      </div>
      <div className="flex gap-2">
        <span className="h-3 w-3 rounded-full bg-green-300" />
        <span className="h-3 w-3 rounded-full bg-slate-200" />
      </div>
    </div>

    <div className="grid gap-3 md:grid-cols-[1.15fr_0.85fr]">
      <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50/80 p-3">
        <motion.div
          className="h-20 rounded-2xl bg-gradient-to-r from-green-100 via-green-50 to-slate-100"
          animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
          transition={{ duration: 5, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
          style={{ backgroundSize: "200% 200%" }}
        />
        <div className="grid grid-cols-2 gap-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <motion.div
              key={index}
              className="h-11 rounded-xl bg-white shadow-sm ring-1 ring-slate-200"
              animate={{ y: [0, index % 2 === 0 ? -4 : 4, 0] }}
              transition={{ duration: 2 + index * 0.2, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }}
            />
          ))}
        </div>
      </div>

      <div className="space-y-3 rounded-2xl border border-slate-200 bg-white/80 p-3">
        {Array.from({ length: 4 }).map((_, index) => (
          <motion.div
            key={index}
            className="h-10 rounded-xl bg-slate-100"
            animate={{ opacity: [0.45, 1, 0.45], x: [0, 3, 0] }}
            transition={{ duration: 1.7 + index * 0.15, repeat: Number.POSITIVE_INFINITY }}
          />
        ))}
      </div>
    </div>

    <motion.div
      className="pointer-events-none absolute inset-x-10 bottom-4 h-1 rounded-full bg-gradient-to-r from-transparent via-green-300/70 to-transparent blur-sm"
      animate={{ opacity: [0.15, 0.65, 0.15], scaleX: [0.8, 1.1, 0.8] }}
      transition={{ duration: 2.3, repeat: Number.POSITIVE_INFINITY }}
    />
  </div>
);

const MovingVanIllustration = () => (
  <div className="relative mx-auto w-full max-w-[24rem] overflow-hidden rounded-[2rem] border border-green-200/60 bg-[radial-gradient(circle_at_top,_rgba(13,147,3,0.18),_transparent_52%),linear-gradient(180deg,_rgba(255,255,255,0.98),_rgba(240,253,244,0.95))] p-5 shadow-[0_30px_90px_-50px_rgba(13,147,3,0.65)]">
    <div className="absolute inset-x-0 bottom-7 h-px bg-gradient-to-r from-transparent via-green-300/60 to-transparent" />
    <motion.div
      className="absolute inset-x-6 bottom-5 h-2 rounded-full bg-green-200/70 blur-md"
      animate={{ scaleX: [0.8, 1.08, 0.8], opacity: [0.3, 0.7, 0.3] }}
      transition={{ duration: 1.8, repeat: Number.POSITIVE_INFINITY }}
    />

    <motion.div
      className="relative mt-10"
      animate={{ x: [-14, 14, -14] }}
      transition={{ duration: 2.8, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }}
    >
      <svg viewBox="0 0 420 220" className="w-full" role="img" aria-label="Animated delivery van and dashboard loading illustration">
        <motion.g
          animate={{ y: [0, -3, 0] }}
          transition={{ duration: 1.4, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }}
        >
          <rect x="66" y="84" width="194" height="82" rx="16" fill="#0d9303" />
          <rect x="86" y="101" width="68" height="48" rx="10" fill="#86efac" />
          <rect x="270" y="98" width="80" height="68" rx="12" fill="#dcfce7" />
          <path d="M260 110h42l31 28v28h-73z" fill="#16a34a" />
          <rect x="291" y="116" width="28" height="20" rx="6" fill="#f0fdf4" />
          <rect x="121" y="72" width="95" height="22" rx="11" fill="#1E293B" opacity="0.08" />
          <circle cx="126" cy="170" r="24" fill="#1E293B" />
          <circle cx="314" cy="170" r="24" fill="#1E293B" />
          <circle cx="126" cy="170" r="11" fill="#E2E8F0" />
          <circle cx="314" cy="170" r="11" fill="#E2E8F0" />
        </motion.g>

        <motion.g
          animate={{ x: [0, 24, 0], opacity: [0.2, 0.65, 0.2] }}
          transition={{ duration: 1.3, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
        >
          <path d="M28 112h42" stroke="#86efac" strokeWidth="10" strokeLinecap="round" />
          <path d="M14 136h56" stroke="#bbf7d0" strokeWidth="8" strokeLinecap="round" />
          <path d="M34 88h28" stroke="#dcfce7" strokeWidth="7" strokeLinecap="round" />
        </motion.g>
      </svg>
    </motion.div>

    <div className="mt-4 grid grid-cols-3 gap-3">
      {Array.from({ length: 3 }).map((_, index) => (
        <motion.div
          key={index}
          className="rounded-2xl border border-white/70 bg-white/80 p-3"
          animate={{ y: [0, index === 1 ? -6 : -3, 0] }}
          transition={{ duration: 2 + index * 0.25, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }}
        >
          <div className="h-2.5 w-10 rounded-full bg-green-200" />
          <div className="mt-2 h-8 rounded-xl bg-slate-100" />
        </motion.div>
      ))}
    </div>
  </div>
);

const IllustratedLoadingVariant = ({
  mode = "login",
  title,
  subtitle,
  badgeLabel,
}: Required<Pick<AppLoadingScreenProps, "mode">> & Pick<AppLoadingScreenProps, "title" | "subtitle" | "badgeLabel">) => (
  <div className="fixed inset-0 z-[100] overflow-hidden bg-[linear-gradient(180deg,_rgba(240,253,244,0.96),_rgba(255,255,255,0.98))] px-4 py-8">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(13,147,3,0.18),_transparent_32%),radial-gradient(circle_at_bottom_right,_rgba(15,23,42,0.08),_transparent_26%)]" />
    <div className="relative mx-auto flex min-h-full w-full max-w-6xl items-center justify-center">
      <div className="grid w-full items-center gap-8 lg:grid-cols-[0.95fr_1.05fr]">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="inline-flex items-center rounded-full border border-green-200 bg-white/80 px-4 py-2 text-xs font-semibold uppercase tracking-[0.28em] text-green-700 shadow-sm">
            {badgeLabel}
          </div>
          <div className="space-y-3">
            <h1 className="max-w-xl font-serif text-4xl font-semibold leading-tight text-slate-900 md:text-5xl">
              {title || (mode === "register" ? "Setting up your account..." : "Loading platform features... preparing your dashboard...")}
            </h1>
            <p className="max-w-xl text-base leading-7 text-slate-600 md:text-lg">
              {subtitle || (mode === "register"
                ? "We are creating your profile, wiring your workspace, and unlocking the marketplace tools tied to your account."
                : "We are restoring your secure session, syncing your tools, and staging the dashboard experience around your role.")}
            </p>
          </div>

          <div className="grid gap-3">
            {loadingStages.map((stage, index) => (
              <motion.div
                key={stage}
                className="flex items-center gap-3 rounded-2xl border border-white/80 bg-white/75 px-4 py-3 shadow-sm backdrop-blur"
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <motion.span
                  className="h-2.5 w-2.5 rounded-full bg-green-500"
                  animate={{ scale: [1, 1.5, 1], opacity: [0.45, 1, 0.45] }}
                  transition={{ duration: 1.25, repeat: Number.POSITIVE_INFINITY, delay: index * 0.18 }}
                />
                <span className="text-sm font-medium text-slate-700">{stage}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-5"
        >
          <MovingVanIllustration />
          <DashboardWireframe />
        </motion.div>
      </div>
    </div>
  </div>
);

const ShellLoadingVariant = () => (
  <div className="min-h-screen bg-background px-4 py-4 md:px-6 md:py-6">
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="rounded-3xl border bg-card p-4 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <Skeleton className="h-8 w-28 rounded-xl" />
          <div className="flex gap-2">
            <Skeleton className="h-8 w-8 rounded-full" />
            <Skeleton className="h-8 w-20 rounded-full" />
          </div>
        </div>
      </div>

      <div className="rounded-3xl border bg-card p-4 shadow-sm">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="mt-3 h-10 w-full rounded-xl" />
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="rounded-3xl border bg-card p-4 shadow-sm">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="mt-3 h-8 w-20" />
            <Skeleton className="mt-3 h-3 w-28" />
          </div>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="rounded-3xl border bg-card p-4 shadow-sm">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="mt-4 h-52 w-full rounded-2xl" />
        </div>
        <div className="rounded-3xl border bg-card p-4 shadow-sm">
          <Skeleton className="h-5 w-28" />
          <div className="mt-4 space-y-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-14 w-full rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const AppLoadingScreen = ({ variant = "shell", mode = "login", title, subtitle, badgeLabel }: AppLoadingScreenProps) => {
  if (variant === "auth") {
    return (
      <IllustratedLoadingVariant
        mode={mode}
        title={title}
        subtitle={subtitle}
        badgeLabel={badgeLabel || (mode === "register" ? "Onboarding" : "Secure access")}
      />
    );
  }

  if (variant === "data") {
    return (
      <IllustratedLoadingVariant
        mode={mode}
        title={title || "Loading live data..."}
        subtitle={subtitle || "We are pulling the latest records from the database and preparing this page for use."}
        badgeLabel={badgeLabel || "Live data"}
      />
    );
  }

  return <ShellLoadingVariant />;
};

export default AppLoadingScreen;
